import React from "react"
import { matchPath, Redirect, useLocation } from "react-router-dom"
import { PageLoading } from "#/features/global-ui/page-layout"
import { useConsumerRoutesEnabled } from "#/services/launchdarkly/useConsumerRoutesEnabled"
import { useIsPaywalled } from "#/services/user/hooks/useSubscriptionStatus"
import { Paths } from "#/utils/Paths"

/**
 * Where a walled member lands. Three places have to agree on it — the route that mounts the
 * page, the allow-list entry below, and the redirect target — and the invariant only holds if
 * they read the same constant: rename any one of the three literals on its own and the rest
 * still typecheck, leaving the gate redirecting to a path it then walls.
 */
export const PAYWALL_PATH = "/paywall"

/** What the gate hands the paywall page so it can put the member back where they were asked for. */
export type PaywallLocationState = {
  from?: { pathname: string; search: string; hash: string }
}

type AllowedPath = {
  path: string
  /** `prefix` matches the path itself and everything below it, on segment boundaries. */
  match: "exact" | "prefix"
}

/**
 * The only paths a paywalled member may still reach; everything else authenticated is
 * walled. One list rather than a wrapper on each route, so the default is "walled" and a
 * route added tomorrow is covered without anyone remembering this file exists.
 */
export const PAYWALL_ALLOW_LIST: readonly AllowedPath[] = [
  { path: "/", match: "exact" },
  { path: "/account", match: "prefix" },
  { path: "/consumer/billing-detail", match: "exact" },
  // Pre-rename path, which redirects to /consumer/billing-detail. Walling it would
  // intercept that redirect rather than let it resolve to an allowed page.
  { path: "/consumer/subscription-billing", match: "exact" },
  { path: "/consumer/faq", match: "exact" },
  // The subscription funnel. Root.tsx mounts it above the authenticated split, out of this
  // gate's reach entirely — listed anyway so a future re-mount cannot wall off the one path
  // a member walks to stop being paywalled.
  { path: "/consumer/payment", match: "prefix" },
  // Internal tooling rather than member content, and the one surface for inspecting a walled
  // member's own state — walling it would take that away just when it is wanted. Prefix so the
  // section's root page and every tool under it come along. App.tsx registers the route outside
  // production only, so in production this allows a path that simply 404s.
  { path: Paths.DEV_TOOLS, match: "prefix" },
  // The redirect target, so the gate cannot send a member to a page it then walls.
  { path: PAYWALL_PATH, match: "exact" }
]

// The router's own matcher, not a second one: `matchPath` is what decides which page actually
// renders, so borrowing it is the only way the allow-list cannot drift from the route table.
// It gives the three properties this needs for free — case-insensitive (`sensitive: false`),
// trailing-slash tolerant (`strict: false`), and terminated on a segment boundary for a prefix
// match, so `/accounts` is not read as something under `/account`.
function isPaywallAllowedPath(pathname: string): boolean {
  return PAYWALL_ALLOW_LIST.some(({ path, match }) => matchPath(pathname, { path, exact: match === "exact" }) !== null)
}

type PaywallGateProps = { children: React.ReactNode }

/**
 * Redirects a paywalled consumer — one whose subscription is anything other than active — to
 * {@link PAYWALL_PATH} for every path outside {@link PAYWALL_ALLOW_LIST}. Wraps a route table
 * rather than individual routes, so a deep link, a hard reload and an in-app click are all
 * caught the same way.
 *
 * Mounted INSIDE the navigation chrome (see App.tsx): the loading and redirect branches replace
 * whatever this wraps, and a member kept on their navigation while the verdict lands is the
 * difference between a paywall and a broken app.
 */
export function PaywallGate({ children }: PaywallGateProps): JSX.Element {
  const { pathname, search, hash } = useLocation()
  // Rides the same LaunchDarkly flag as the /paywall route in App.tsx, so the gate can never
  // exist without its redirect target and 404 the member it walls.
  //
  // Passed DOWN as `enabled` rather than used to swap this component out for a Fragment: the
  // flag is `false` while LaunchDarkly initialization is in flight and flips when the answer
  // arrives, which can be after App has mounted (AppContainer gives up waiting after 3s). A
  // swap at this position would then destroy and rebuild everything below — navigation,
  // current page, open modals, half-typed forms, scroll position. As an argument it changes
  // only whether the queries fire, and with it false the hook is inert: no request, no verdict,
  // every route rendered untouched.
  const consumerRoutesEnabled = useConsumerRoutesEnabled()
  const { isPaywalled, isLoading, isRevalidating } = useIsPaywalled(consumerRoutesEnabled)
  const pageKey = pathname + search + hash
  const renderedPage = React.useRef<string | null>(null)
  React.useEffect(() => {
    if (!isLoading && !isRevalidating && !isPaywalled) renderedPage.current = pageKey
  }, [pageKey, isLoading, isRevalidating, isPaywalled])

  // Answered before the loading state: an allow-listed path renders whichever way the verdict
  // lands, so there is nothing to wait for — and the billing page and payment funnel in
  // particular must never sit behind a spinner.
  if (isPaywallAllowedPath(pathname)) return <>{children}</>
  // Held rather than rendered: painting a walled page and pulling it back a tick later is a
  // flash of the very content being charged for. `isRevalidating` is here too so a member who
  // has just paid on the billing page — which invalidates this verdict as it unmounts — waits
  // for the new answer instead of being bounced to /paywall on the stale one.
  if (isLoading || isRevalidating) {
    // Retrying a failed status read must not discard answers on a page already admitted.
    return renderedPage.current === pageKey ? <>{children}</> : <PageLoading />
  }
  // `isPaywalled` is already false when the status read failed — `useIsPaywalled` fails open,
  // so an outage renders the requested page instead of walling a member who has paid. This
  // gate is UX; entitlement is enforced server-side.
  if (isPaywalled) {
    // Carries the requested location so subscribing can return the member to it. A `Redirect`
    // replaces the history entry, so without this the destination they asked for is simply gone.
    return <Redirect to={{ pathname: PAYWALL_PATH, state: { from: { pathname, search, hash } } }} />
  }
  return <>{children}</>
}
